import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, XCircle, ArrowLeft, Package } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setError('Session ID non trovato nell\'URL');
      return;
    }

    // Start polling for payment status
    pollPaymentStatus(sessionId);
  }, [sessionId]);

  const fetchOrderData = async (orderId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}`
      );
      
      if (response.ok) {
        const order = await response.json();
        setOrderData(order);
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setError('Timeout nel controllo dello stato del pagamento. Controlla la tua email per la conferma.');
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/status/${sessionId}`
      );
      
      if (!response.ok) {
        throw new Error('Errore nel controllo dello stato del pagamento');
      }

      const data = await response.json();
      setPaymentData(data);
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        // Fetch order data using the order_id from metadata
        if (data.metadata?.order_id) {
          fetchOrderData(data.metadata.order_id);
        }
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setError('La sessione di pagamento è scaduta');
        return;
      } else if (data.payment_status === 'failed' || data.status === 'failed') {
        setPaymentStatus('failed');
        setError('Il pagamento non è riuscito');
        return;
      }

      // If payment is still pending, continue polling
      setPaymentStatus('pending');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Errore nel controllo dello stato del pagamento');
      setPaymentStatus('error');
    }
  };

  const renderStatusIcon = () => {
    switch (paymentStatus) {
      case 'checking':
      case 'pending':
        return <Clock className="w-16 h-16 text-yellow-500 animate-pulse" />;
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'failed':
      case 'expired':
      case 'error':
      case 'timeout':
        return <XCircle className="w-16 h-16 text-red-500" />;
      default:
        return <Clock className="w-16 h-16 text-gray-400" />;
    }
  };

  const renderStatusMessage = () => {
    switch (paymentStatus) {
      case 'checking':
        return {
          title: 'Controllo del pagamento in corso...',
          description: 'Stiamo verificando lo stato del tuo pagamento. Attendi un momento.'
        };
      case 'pending':
        return {
          title: 'Pagamento in elaborazione...',
          description: 'Il tuo pagamento è in corso di elaborazione. Questo potrebbe richiedere alcuni istanti.'
        };
      case 'success':
        return {
          title: 'Pagamento completato con successo!',
          description: 'Grazie per il tuo acquisto. Riceverai presto una email di conferma con i dettagli del tuo ordine.'
        };
      case 'failed':
        return {
          title: 'Pagamento fallito',
          description: error || 'Il pagamento non è andato a buon fine. Riprova o contatta il supporto.'
        };
      case 'expired':
        return {
          title: 'Sessione scaduta',
          description: 'La sessione di pagamento è scaduta. Riprova a effettuare l\'ordine.'
        };
      default:
        return {
          title: 'Errore',
          description: error || 'Si è verificato un errore imprevisto.'
        };
    }
  };

  const { title, description } = renderStatusMessage();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          {renderStatusIcon()}
        </div>
        
        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {title}
        </h1>
        
        <p className="text-gray-600 mb-6">
          {description}
        </p>

        {paymentData && paymentStatus === 'success' && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <Package className="w-4 h-4 mr-2" />
              Dettagli Ordine
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              {orderData?.orderNumber && (
                <p><span className="font-medium">Numero Ordine:</span> {orderData.orderNumber}</p>
              )}
              <p><span className="font-medium">ID Pagamento:</span> {paymentData.payment_id}</p>
              <p><span className="font-medium">Importo:</span> €{paymentData.amount.toFixed(2)}</p>
              <p><span className="font-medium">Valuta:</span> {paymentData.currency}</p>
              {paymentData.metadata?.package_name && (
                <p><span className="font-medium">Pacchetto:</span> {paymentData.metadata.package_name}</p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-3">
          {paymentStatus === 'success' ? (
            <>
              {orderData?.orderNumber ? (
                <button
                  onClick={() => navigate(`/order-confirmation/${orderData.orderNumber}`)}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  Visualizza Ordine
                </button>
              ) : (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  Vai al Dashboard
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alla Homepage
              </button>
            </>
          ) : paymentStatus === 'failed' || paymentStatus === 'expired' || paymentStatus === 'error' || paymentStatus === 'timeout' ? (
            <>
              <button
                onClick={() => navigate('/pagamento')}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Riprova Pagamento
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Torna alla Homepage
              </button>
            </>
          ) : (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent"></div>
            </div>
          )}
        </div>

        {sessionId && (
          <p className="text-xs text-gray-400 mt-4">
            Session ID: {sessionId}
          </p>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;